/** @jsxImportSource @emotion/react */
'use memo'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import type { OrgRegistrationBadge } from '@sportninja/common/features/organization/registration/utils/cardModel'
import { captionXSmall, weightSemiBold } from 'src/components/css'

interface Props {
  badge: OrgRegistrationBadge
  label: string
}

/**
 * A registration's status pill — Open (with a live pulse), Closing Soon,
 * Upcoming or Closed. Shared by the card and the list view.
 */
const OrgRegistrationStatusChip = ({ badge, label }: Props) => {
  'use memo'

  return (
    <Chip badge={badge}>
      <Dot badge={badge} aria-hidden='true' />
      <ChipLabel>{label}</ChipLabel>
    </Chip>
  )
}

export default OrgRegistrationStatusChip

const BADGE_COLORS: Record<
  OrgRegistrationBadge,
  { text: string; fill: string }
> = {
  open: { text: colors.SUCCESS_100, fill: `${colors.SUCCESS_200}29` },
  closingSoon: { text: colors.WARNING_200, fill: `${colors.WARNING_200}24` },
  upcoming: { text: colors.INFO_100, fill: `${colors.INFO_300}29` },
  past: { text: colors.NEUTRAL_40, fill: colors.SECONDARY_200 },
}

const livePulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${colors.SUCCESS_100}8C; }
  70% { box-shadow: 0 0 0 6px ${colors.SUCCESS_100}00; }
  100% { box-shadow: 0 0 0 0 ${colors.SUCCESS_100}00; }
`

/*
 * A fixed height and a 1 line-height, so the pill's size and the text's centre
 * come from the box, not from the font's ascent/descent, which differ by OS,
 * tenant font and fallback font. Matches the sport chip beside it on the card.
 */
const Chip = styled.span<{ badge: OrgRegistrationBadge }>`
  ${captionXSmall}
  ${weightSemiBold}
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  height: 24px;
  padding: 0 10px;
  line-height: 1;
  border-radius: 999px;
  white-space: nowrap;
  color: ${({ badge }) => BADGE_COLORS[badge].text};
  background: ${({ badge }) => BADGE_COLORS[badge].fill};
`

/*
 * Trims the line box to the capitals where supported, so uppercase text sits
 * on the true centre. The trailing letter-spacing is taken back, so the
 * padding reads even on both sides.
 */
const ChipLabel = styled.span`
  display: block;
  margin-right: -1.2px;
  text-box: trim-both cap alphabetic;
`

const Dot = styled.span<{ badge: OrgRegistrationBadge }>`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ badge }) => BADGE_COLORS[badge].text};
  animation: ${({ badge }) => (badge === 'open' ? livePulse : 'none')} 1.8s
    ease-out infinite;
`
